import { inject, Service, service } from './di'
import { SessionStorageService } from './SessionStorageService'
import { Country, LocationService } from './LocationService'
import { ConfigService } from './ConfigService'
import { DateTimeService } from './DateTimeService'

interface ActiveBanner {
    id: string
    startDate: Date
    endDate: Date
    countries: Country[]
    type: 'giveaway' | 'normal'
    topBarBanner?: boolean
}

export const ACTIVE_BANNERS: ActiveBanner[] = [
    {
        id: 'black_friday',
        startDate: new Date('November 3, 2025 00:00:01'),
        endDate: new Date('December 7, 2025 23:59:59'),
        countries: [Country.CANADA, Country.USA],
        type: 'normal',
    },
    {
        id: 'giveaway',
        startDate: new Date('November 17, 2025 00:00:01'),
        endDate: new Date('December 5, 2025 23:59:59'),
        countries: [Country.CANADA, Country.USA],
        type: 'giveaway',
        topBarBanner: true,
    },
]

@service('BannerService')
export class BannerService extends Service {
    @inject config: ConfigService
    @inject location: LocationService
    @inject dateService: DateTimeService

    isSmartNews() {
        const sessionStorageService = SessionStorageService.getInstance()
        const synd = sessionStorageService.getItem('synd')
        return synd === 'smartnews'
    }

    shouldDisplayCarousel() {
        return (
            ACTIVE_BANNERS.reduce((acc, banner) => {
                if (this.isBannerVisible(banner)) return acc + 1
                return acc
            }, 0) > 1
        )
    }

    getGiveawayLink() {
        const dateNow = this.dateService.getNowEST()
        const day = dateNow.day
        const month = dateNow.month
        const locale = this.location.locale as 'en-ca' | 'en-us' | 'fr-ca'

        const linksForDay = GIVEAWAY_LINKS[`${day}-${month}`]
        return linksForDay ? linksForDay[locale] : '/'
    }

    isBannerVisible(banner: ActiveBanner): boolean {
        if (banner.type === 'giveaway') {
            const urlParams = new URLSearchParams(window.location.search)
            if (urlParams.has('giveaway')) return true
        }
        if (banner.type === 'normal') {
            const urlParams = new URLSearchParams(window.location.search)
            if (urlParams.has('blackfriday')) return true
        }

        return this.hasValidDates(banner)
    }

    hasValidDates(banner: ActiveBanner): boolean {
        const dateNow = this.dateService.getNowEST()
        const dateStart = this.dateService.fromJsDateEST(banner.startDate)
        const dateEnd = this.dateService.fromJsDateEST(banner.endDate)
        return (
            dateNow >= dateStart &&
            dateNow <= dateEnd &&
            banner.countries.indexOf(this.location.country) !== -1
        )
    }

    shouldShowProudlyCanadianBanner() {
        // @ts-ignore
        const proudlyCanadianBanner = ACTIVE_BANNERS.find(
            (banner: ActiveBanner) => banner.id === 'proudly-canadian'
        )
        return (
            proudlyCanadianBanner && this.isBannerVisible(proudlyCanadianBanner)
        )
    }

    getTopBarBanner() {
        // @ts-ignore
        return ACTIVE_BANNERS.find(
            (banner: ActiveBanner) =>
                banner.topBarBanner && this.isBannerVisible(banner)
        )
    }
}

const GIVEAWAY_LINKS: Record<
    `${number}-${number}`,
    {
        'en-ca': string
        'fr-ca': string
        'en-us': string
    }
> = {
    '17-11': {
        'en-ca':
            'https://gleam.io/J7JEu/canada-flipp-black-friday-giveaway-day-1',
        'fr-ca':
            'https://gleam.io/O3Gq8/canada-flipp-concours-vendredi-fou-jour-1',
        'en-us': 'https://gleam.io/cFA12/us-flipp-black-friday-giveaway-day-1',
    },
    '18-11': {
        'en-ca':
            'https://gleam.io/ftvf5/canada-flipp-black-friday-giveaway-day-2',
        'fr-ca':
            'https://gleam.io/1gdKd/canada-flipp-concours-vendredi-fou-jour-2',
        'en-us': 'https://gleam.io/9gQfb/us-flipp-black-friday-giveaway-day-2',
    },
    '19-11': {
        'en-ca':
            'https://gleam.io/evcIP/canada-flipp-black-friday-giveaway-day-3',
        'fr-ca':
            'https://gleam.io/uD7Av/canada-flipp-concours-vendredi-fou-jour-3',
        'en-us': 'https://gleam.io/OuSfv/us-flipp-black-friday-giveaway-day-3',
    },
    '20-11': {
        'en-ca':
            'https://gleam.io/Q4fFk/canada-flipp-black-friday-giveaway-day-4',
        'fr-ca':
            'https://gleam.io/Pnr5X/canada-flipp-concours-vendredi-fou-jour-4',
        'en-us': 'https://gleam.io/afKQh/us-flipp-black-friday-giveaway-day-4',
    },
    '21-11': {
        'en-ca':
            'https://gleam.io/ZnD6W/canada-flipp-black-friday-giveaway-day-5',
        'fr-ca':
            'https://gleam.io/xr7oi/canada-flipp-concours-vendredi-fou-jour-5',
        'en-us': 'https://gleam.io/yn3kT/us-flipp-black-friday-giveaway-day-5',
    },
    '22-11': {
        'en-ca':
            'https://gleam.io/AOhmH/canada-flipp-black-friday-giveaway-day-6',
        'fr-ca':
            'https://gleam.io/8dZhW/canada-flipp-concours-vendredi-fou-jour-6',
        'en-us': 'https://gleam.io/YaOvA/us-flipp-black-friday-giveaway-day-6',
    },
    '23-11': {
        'en-ca':
            'https://gleam.io/8cCOT/canada-flipp-black-friday-giveaway-day-7',
        'fr-ca':
            'https://gleam.io/FEZDK/canada-flipp-concours-vendredi-fou-jour-7',
        'en-us': 'https://gleam.io/xGoGs/us-flipp-black-friday-giveaway-day-7',
    },
    '24-11': {
        'en-ca':
            'https://gleam.io/y9gGv/canada-flipp-black-friday-giveaway-day-8',
        'fr-ca':
            'https://gleam.io/OU9xC/canada-flipp-concours-vendredi-fou-jour-8',
        'en-us': 'https://gleam.io/DssDb/us-flipp-black-friday-giveaway-day-8',
    },
    '25-11': {
        'en-ca':
            'https://gleam.io/ceD5k/canada-flipp-black-friday-giveaway-day-9',
        'fr-ca':
            'https://gleam.io/7pulO/canada-flipp-concours-vendredi-fou-jour-9',
        'en-us': 'https://gleam.io/rD8mW/us-flipp-black-friday-giveaway-day-9',
    },
    '26-11': {
        'en-ca':
            'https://gleam.io/x3UE7/canada-flipp-black-friday-giveaway-day-10',
        'fr-ca':
            'https://gleam.io/o3fZA/canada-flipp-concours-vendredi-fou-jour-10',
        'en-us': 'https://gleam.io/tzmhC/us-flipp-black-friday-giveaway-day-10',
    },
    '27-11': {
        'en-ca':
            'https://gleam.io/jelc3/canada-flipp-black-friday-giveaway-day-11',
        'fr-ca':
            'https://gleam.io/mH132/canada-flipp-concours-vendredi-fou-jour-11',
        'en-us': 'https://gleam.io/r01nF/us-flipp-black-friday-giveaway-day-11',
    },
    '28-11': {
        'en-ca':
            'https://gleam.io/zg1ck/canada-flipp-black-friday-giveaway-day-12',
        'fr-ca':
            'https://gleam.io/OPcid/canada-flipp-concours-vendredi-fou-jour-12',
        'en-us': 'https://gleam.io/iHChx/us-flipp-black-friday-giveaway-day-12',
    },
    '29-11': {
        'en-ca':
            'https://gleam.io/ZWDzu/canada-flipp-black-friday-giveaway-day-13',
        'fr-ca':
            'https://gleam.io/OhUzG/canada-flipp-concours-vendredi-fou-jour-13',
        'en-us': 'https://gleam.io/PyAd5/us-flipp-black-friday-giveaway-day-13',
    },
    '30-11': {
        'en-ca':
            'https://gleam.io/AbebS/canada-flipp-black-friday-giveaway-day-14',
        'fr-ca':
            'https://gleam.io/5FuIP/canada-flipp-concours-vendredi-fou-jour-14',
        'en-us': 'https://gleam.io/ftnQC/us-flipp-black-friday-giveaway-day-14',
    },
    '1-12': {
        'en-ca':
            'https://gleam.io/pDCym/canada-flipp-black-friday-giveaway-day-15',
        'fr-ca':
            'https://gleam.io/2UeG1/canada-flipp-concours-vendredi-fou-jour-15',
        'en-us': 'https://gleam.io/CR934/us-flipp-black-friday-giveaway-day-15',
    },
    '2-12': {
        'en-ca':
            'https://gleam.io/iEott/canada-flipp-black-friday-giveaway-day-16',
        'fr-ca':
            'https://gleam.io/G8TVf/canada-flipp-concours-vendredi-fou-jour-16',
        'en-us': 'https://gleam.io/KigV4/us-flipp-black-friday-giveaway-day-16',
    },
    '3-12': {
        'en-ca':
            'https://gleam.io/92IQy/canada-flipp-black-friday-giveaway-day-17',
        'fr-ca':
            'https://gleam.io/rKCv2/canada-flipp-concours-vendredi-fou-jour-17',
        'en-us': 'https://gleam.io/EWhXJ/us-flipp-black-friday-giveaway-day-17',
    },
    '4-12': {
        'en-ca':
            'https://gleam.io/7XjK0/canada-flipp-black-friday-giveaway-day-18',
        'fr-ca':
            'https://gleam.io/Rk9Cs/canada-flipp-concours-vendredi-fou-jour-18',
        'en-us': 'https://gleam.io/t6Gwp/us-flipp-black-friday-giveaway-day-18',
    },
    '5-12': {
        'en-ca':
            'https://gleam.io/5SsTM/canada-flipp-black-friday-giveaway-day-19',
        'fr-ca':
            'https://gleam.io/2UiWS/canada-flipp-concours-vendredi-fou-jour-19',
        'en-us': 'https://gleam.io/VoVVE/us-flipp-black-friday-giveaway-day-19',
    },
} as const
